import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {CustomError} from "../../../shared/interfaces/CustomError.interface";
import {ListsService} from "../../../shared/services/list.service";
import {WorksService} from "../../../shared/services/works.service";
import {WorksDto, WorksModel} from "src/app/models/WorksModel";
import {List} from "src/app/models/List";
import {User} from "src/app/modules/auth/models/User";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {ModalService} from "../../services/modal.service";
import {Subscription} from "rxjs";
import {ToastService} from "../../services/toast.service";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {TranslateService, TranslateModule} from "@ngx-translate/core";
import {WorkBulkActionsService} from "../../../shared/services/work-bulk-actions.service";

@Component({
  selector: "app-create-list",
  templateUrl: "./create-list.component.html",
  styleUrls: ["./create-list.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("fadeOut", [
      state("void", style({opacity: 1})),
      transition(":leave", [animate("0.3s", style({opacity: 0}))]),
    ]),
  ],
  imports: [FormsModule, ReactiveFormsModule, MatSlideToggle, TranslateModule],
})
export class CreateListComponent implements OnInit, OnDestroy {
  @Input() user!: User;
  createList: FormGroup;
  public error?: CustomError;
  public submitText?: string;
  selectedImageUrl: string | ArrayBuffer | null = "";
  private pendingItem?: WorksModel;
  private pendingItems: WorksModel[] = [];
  private hasPendingSelection = false;
  private addedToText?: string;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly translate: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly listsService: ListsService,
    private readonly worksService: WorksService,
    private readonly workBulkActionsService: WorkBulkActionsService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.createList = this.formBuilder.group({
      name: ["", Validators.required],
      description: [""],
      isPrivate: [false],
      isCollaborative: [false],
    });
  }

  ngOnInit(): void {
    this.setSubmitText("CREATE_COLLECTION");
    this.setAddedToText("ADDED_TO");

    const translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.setSubmitText("CREATE_COLLECTION");
      this.setAddedToText("ADDED_TO");
      this.cdr.markForCheck();
    });
    this.subscriptions.push(translateSubscription);

    const modalItemSubscription = this.modalService.modalItem$.subscribe({
      next: (modalData) => {
        if (modalData?.modalId === "addList") {
          const item = modalData.item;
          this.pendingItem =
            item && "provider" in item && "type" in item
              ? (item as WorksModel)
              : undefined;
          this.pendingItems =
            modalData.items ?? (this.pendingItem ? [this.pendingItem] : []);
          this.hasPendingSelection = modalData.items !== undefined;
        }
      },
    });
    this.subscriptions.push(modalItemSubscription);
  }

  private setAddedToText(key: string): void {
    const sub = this.translate.get(key).subscribe((text: string) => {
      this.addedToText = text;
    });
    this.subscriptions.push(sub);
  }

  private setSubmitText(key: string): void {
    const translateSubscription = this.translate
      .get(key)
      .subscribe((translatedText) => {
        this.submitText = translatedText;
        this.cdr.markForCheck();
      });
    this.subscriptions.push(translateSubscription);
  }

  onSubmit() {
    if (this.createList.invalid) {
      this.error = {error: {error: "FILL_ALL_FIELDS"}};
      return;
    }

    this.setSubmitText("CREATING_COLLECTION");

    const list = {
      name: this.createList.value.name,
      description: this.createList.value.description,
      isPrivate: this.createList.value.isPrivate ?? false,
      isCollaborative: this.createList.value.isCollaborative ?? false,
    };

    const addListSubscription = this.listsService
      .addList(this.user, list)
      .subscribe({
        next: (newList) => {
          this.setSubmitText("CREATE_COLLECTION");
          this.createList.reset();
          this.createList.get("isPrivate")?.enable();
          this.createList.get("isCollaborative")?.enable();
          this.cdr.markForCheck();

          if (this.pendingItem) {
            if (this.hasPendingSelection) {
              this.addWorksToNewList(newList);
            } else {
              this.addWorkToNewList(newList);
            }
          } else {
            this.toastService.sendToast({
              text: "LIST_CREATED",
              classes: "bg-form-success",
            });
            setTimeout(() => {
              this.modalService.closeModal("addList");
            }, 3000);
          }
        },
        error: (error: CustomError) => {
          this.setSubmitText("CREATE_COLLECTION");
          this.error = error;
          this.toastService.sendToast({
            text: this.error.error.error,
            classes: "bg-form-error",
          });
        },
      });
    this.subscriptions.push(addListSubscription);
  }

  private addWorkToNewList(newList: List): void {
    const item = this.pendingItem!;
    const worksDto: WorksDto = {
      workId: item._id,
    };

    const addWorkSubscription = this.worksService
      .addWorkByType(worksDto, item.type, this.user, newList._id)
      .subscribe({
        next: (updatedWork: WorksModel) => {
          const title = updatedWork.title ?? item.title ?? "";
          this.sendWorkAddedToast(title, newList);
          this.cdr.markForCheck();
          this.modalService.closeModal("addList");
        },
        error: () => {
          this.toastService.sendToast({
            text: "LIST_CREATED",
            classes: "bg-form-success",
          });
          this.cdr.markForCheck();
          setTimeout(() => {
            this.modalService.closeModal("addList");
          }, 3000);
        },
      });
    this.subscriptions.push(addWorkSubscription);
  }

  private addWorksToNewList(newList: List): void {
    const references = this.pendingItems.map((item) => ({
      _id: item._id,
      type: item.type,
    }));
    const subscription = this.workBulkActionsService
      .addToList(this.user._id, newList._id, references)
      .subscribe({
        next: (response) => {
          const completedKeys = new Set(
            response.results
              .filter(
                (result) =>
                  result.status === "success" ||
                  result.status === "alreadyInList"
              )
              .map((result) => `${result.work.type}:${result.work._id}`)
          );
          const updatedWorks = this.pendingItems.map((item) =>
            completedKeys.has(`${item.type}:${item._id}`)
              ? this.withList(item, newList)
              : item
          );
          this.modalService.bulkActionCompleted.emit({
            references: references.filter((reference) =>
              completedKeys.has(`${reference.type}:${reference._id}`)
            ),
            removesFromSource: false,
            updatedWorks,
          });
          const allCompleted = completedKeys.size === references.length;
          if (allCompleted && references.length === 1) {
            this.sendWorkAddedToast(this.pendingItem?.title ?? "", newList);
          } else {
            this.toastService.sendToast({
              text: allCompleted
                ? "WORK_SELECTION_ACTION_SUCCESS"
                : "WORK_SELECTION_ACTION_PARTIAL",
              classes: "bg-form-success",
            });
          }
          this.modalService.closeModal("addList");
        },
        error: () => {
          this.toastService.sendToast({
            text: "LIST_CREATED",
            classes: "bg-form-success",
          });
          this.modalService.closeModal("addList");
        },
      });
    this.subscriptions.push(subscription);
  }

  private sendWorkAddedToast(title: string, list: List): void {
    this.toastService.sendToast({
      text: `${title} ${this.addedToText} ${list.name}`,
      translatable: false,
      classes: "bg-form-success",
    });
  }

  private withList(item: WorksModel, list: List): WorksModel {
    const alreadyInList = (item.lists ?? []).some(
      (itemList) => itemList.id === list._id || itemList._id === list._id
    );
    if (alreadyInList) {
      return item;
    }
    return {
      ...item,
      lists: [
        ...(item.lists ?? []),
        {name: list.name, id: list._id, slug: list.slug},
      ],
    };
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}
